import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["unSelectableDays", "outside", "today", "weekEnd"],
      _excluded2 = ["onClick"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React, { useRef } from "react";
import { useDayPicker, useDayRender } from "react-day-picker";
import BaseButton from "../BaseButton/BaseButton";
import { MONTHS, TODAY, WEEKDAYS_LONG, ZERO } from "./constants";
/**
 * The Day component renders each date of the month.
 * It is created because RDP has certain limitations
 * mentioned below:
 *  - If a date is disabled then it is skipped from arrow navigation.
 *  - No way to add `aria-current` for the current date.
 *  - Need to add lot of classes to change the look and feel of the individual day
 *    as per our themes.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";

const Day = _ref => {
  let {
    cssCurrentDay,
    cssDay,
    cssDisabledDay,
    cssSelectedDay,
    date,
    displayMonth
  } = _ref;
  const dayButtonRef = useRef();
  const dayProps = useDayRender(date, displayMonth, dayButtonRef);
  const {
    modifiersStyles,
    modifiersClassNames
  } = useDayPicker();
  const {
    buttonProps,
    activeModifiers
  } = dayProps;

  const {
    unSelectableDays,
    outside,
    today,
    weekEnd = false
  } = activeModifiers,
        otherModifiers = _objectWithoutProperties(activeModifiers, _excluded);

  const {
    onClick: defaultDayClickHandler
  } = buttonProps,
        otherButtonProps = _objectWithoutProperties(buttonProps, _excluded2);
  /**
   * Compute the styles by taking today, weekday and weekend
   * into consideration.
   * @returns {Object} - styles to be applied based on state.
   */


  const getDayButtonStyles = () => {
    const isDateActive = buttonProps["aria-pressed"];
    /**
     * Mocking today date is breaking the functionality
     * of data-fns module and unable to render the
     * calendar hence disabling the else if block
     * @58.
     */

    /* istanbul ignore next */

    if (isDateActive) {
      return cssSelectedDay;
    } else if (today && !isDateActive && !weekEnd) {
      // selectable date, meaning current date is weekday.
      return _objectSpread(_objectSpread({}, cssDay), cssCurrentDay.selectable);
    } else if (today && !isDateActive && weekEnd) {
      // nonSelectable date, meaning current date is weekend.
      return _objectSpread(_objectSpread({}, cssDisabledDay), cssCurrentDay.nonSelectable);
    } else if (weekEnd || unSelectableDays) {
      return cssDisabledDay;
    } else {
      return cssDay;
    }
  };
  /**
   * getModifiersStylesOrClassName checks for each modifier and it is true
   * then it will add the respective class name to the button component.
   * @param {String | Object} - defaultValue - button styles when it is an object.
   *                          button class name when it is a string.
   * @param {Object} - lookUpModifier - User passed modifiers either
   *                   `modifiersClassNames` or `modifiersStyles`.
   * @returns {String | null} - custom class names for BaseButton.
   */


  const getModifiersStylesOrClassName = function (defaultValue) {
    let lookUpModifier = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const otherModifiersList = Object.keys(otherModifiers);
    const otherModifiersLength = otherModifiersList.length;

    if (otherModifiersLength > 0) {
      return otherModifiersList.reduce((updatedValue, eachModifier) => {
        const modifierValue = lookUpModifier[eachModifier];

        if (otherModifiers[eachModifier] && modifierValue) {
          if (typeof defaultValue === "object") {
            updatedValue = _objectSpread(_objectSpread({}, updatedValue), modifierValue);
          } else {
            updatedValue = `${updatedValue} ${modifierValue}`;
          }
        }

        return updatedValue;
      }, defaultValue);
    }

    return defaultValue;
  };
  /**
   * Overriding the `click` handler provided by `RDP`,
   * so that the selection of
   * weekend dates does not happen, when `weekEnd` prop is true.
   */


  const handleWeekEndDateClick = () => null;

  const getDayAriaLabel = () => {
    const formattedDateText = String.prototype.padStart.apply(date.getDate(), [2, ZERO]);
    const month = MONTHS[date.getMonth()];
    const year = date.getFullYear();
    const weekDayName = WEEKDAYS_LONG[date.getDay()];
    const ariaLabelSuffix = today ? `, ${TODAY}` : "";
    const ariaLabel = `${formattedDateText}, ${month} ${year}, ${weekDayName}${ariaLabelSuffix}`;
    return ariaLabel;
  };

  const currentValue = today ? "date" : null;
  const buttonStyles = getDayButtonStyles();
  return outside ? null : _jsx(BaseButton, _objectSpread(_objectSpread({
    ref: dayButtonRef
  }, otherButtonProps), {}, {
    className: getModifiersStylesOrClassName("", modifiersClassNames),
    "aria-disabled": weekEnd || unSelectableDays || null,
    "aria-current": currentValue,
    active: Boolean(buttonProps["aria-pressed"]),
    buttonStyles: getModifiersStylesOrClassName(buttonStyles, modifiersStyles),
    noBox: true,
    onClick: weekEnd || unSelectableDays ? handleWeekEndDateClick : defaultDayClickHandler,
    "aria-label": getDayAriaLabel(),
    children: date.getDate()
  }));
};

Day.propTypes = process.env.NODE_ENV !== "production" ? {
  date: PropTypes.instanceOf(Date).isRequired,
  displayMonth: PropTypes.instanceOf(Date).isRequired,
  cssCurrentDay: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  cssDay: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  cssDisabledDay: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  cssSelectedDay: PropTypes.object // eslint-disable-line react/forbid-prop-types

} : {};
Day.defaultProps = {
  cssCurrentDay: {},
  cssDay: {},
  cssSelectedDay: {},
  cssDisabledDay: {}
};
export default Day;